import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import { API } from "@/lib/api";
import { LogoFull } from "@/components/Logo";
import { StatusPill } from "@/components/AccountShell";
import { TrackingStatus } from "@/components/tracking/TrackingStatus";
import { useLatestRequest } from "@/hooks/useLatestRequest";
import { useAuth } from "@/context/AuthContext";

export default function OrderTracking() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { run, cancel } = useLatestRequest();
  const { user } = useAuth();
  const identity = user?.user_id;
  const load = useCallback(async () => {
    setBusy(true);
    await run((signal) => axios.get(`${API}/orders/${orderId}/tracking`, { signal }), ({ data }) => { setData(data); setError(""); }, (err) => {
      setData(null);
      setError([401, 403, 404].includes(err.response?.status) ? "This order is private. Sign in to the account that placed it, or use the same browser as your guest checkout." : "Tracking could not be refreshed. Check your connection and retry.");
    }, () => setBusy(false));
  }, [orderId, run]);
  useEffect(() => {
    setData(null); setError(""); load();
    const refresh = () => { if (document.visibilityState === "visible") load(); else { cancel(); setData(null); setBusy(false); } };
    const timer = setInterval(refresh, 10000);
    document.addEventListener("visibilitychange", refresh);
    return () => { cancel(); clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [load, cancel, identity]);
  return (
    <div className="min-h-screen bg-cream px-4 py-8 font-body text-ragi-dark sm:px-8" data-testid="order-tracking-page">
      <header className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 border-b border-ragi/15 pb-5">
        <Link to="/" aria-label="Kandula home" data-testid="tracking-home-link"><LogoFull /></Link>
        <Link to={`/order/${orderId}`} className="text-sm font-semibold text-terracotta" data-testid="tracking-receipt-link">Back to order</Link>
      </header>
      <main className="mx-auto max-w-5xl py-10">
        <h1 className="font-display text-4xl font-semibold sm:text-5xl" data-testid="tracking-page-title">Your delivery</h1>
        <div className="my-5 flex flex-wrap items-center justify-between gap-3">
          {data && <div className="flex flex-wrap items-center gap-3"><p className="font-mono text-xs text-terracotta" data-testid="tracking-order-number">{data.order_number}</p><StatusPill status={data.order_status} testId="tracking-order-status" /></div>}
          <button onClick={() => load()} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-ragi/20 px-4 py-2 text-xs font-semibold disabled:opacity-50" data-testid="tracking-refresh-button"><RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />Refresh</button>
        </div>
        {error ? <div role="alert" data-testid="tracking-error"><p className="text-sm text-terracotta">{error}</p><Link to="/account/orders" className="mt-4 inline-block text-sm underline" data-testid="tracking-signin-link">My orders / sign in</Link></div> : data ? <TrackingStatus data={data} /> : <p role="status" data-testid="tracking-loading">Loading tracking…</p>}
        <p className="mt-8 border-t border-ragi/15 pt-4 text-xs text-muted-foreground" data-testid="tracking-privacy-note">Location is private to this order. It disappears when sharing stops or delivery closes. Browser GPS may pause when the rider locks their phone.</p>
      </main>
    </div>
  );
}